import RenderManager from "../rendering/render-manager";
import Utils from "../utils";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";
import SlideManager from "../utils/slide-manager";
import Text from "./Text";
import Transition from "./Transition";
import getTabIndex from "../utils/tab-index";

class Side {
    constructor(side, object) {
        const commonOptions = object.options;
        const options = commonOptions[side];
        const uuid = object.uuid;
        this.side = side;
        this.object = object;
        this.content = options.content || "Enter your text";

        this._fontSize = "fontSize" in options !== undefined ? options.fontSize : this._fontSize;

        this._textShadows = "textShadows" in options ? options.textShadows : this._textShadows;
        this._textOutline = "textOutline" in options ? options.textOutline : this._textOutline;
        this._textBoxBgColor = "textBoxBgColor" in options ? options.textBoxBgColor : this._textBoxBgColor;
        this._textBoxBgOpacity = "textBoxBgOpacity" in options ? options.textBoxBgOpacity : this._textBoxBgOpacity;
        this._textBoxBorderColor = "textBoxBorderColor" in options ? options.textBoxBorderColor : this._textBoxBorderColor;
        this._textBoxBorderWidth = "textBoxBorderWidth" in options ? options.textBoxBorderWidth : this._textBoxBorderWidth;
        this._lineHeight = "lineHeight" in options ? options.lineHeight : this._lineHeight;
        this._definitions = "definitions" in options ? options.definitions : this._definitions;
        this._extraClassname = "extraClassname" in options ? options.extraClassname : this._extraClassname;

        this._placeholder = "placeholder" in options ? options.placeholder : this._placeholder;

        this.textElement = document.createElement('div');
        this.textElement.id = uuid + "_" + side;
        this.textElement.className = `q3-text card-face ${side}`;
        this.textElement.setAttribute("data-btntext", "Flip Over");
        if (options.translations) {
            this.translations = options.translations
        } else {
            this.data.translations = this.translations = {
                EN: options.content
                    ? options.content
                    : "Enter Your Text",
                IND: "Enter Your Text"
            }
        }

    }

    get data() {
        return this.object.data[this.side];
    }
}

class FlashCard extends Text {
    get type2d() { return "FlashCard"; }

    _initDefaultVars() {
        super._initDefaultVars();
        this._fontSize = "28rem";
        this.sides = ["sideA", "sideB"];
    }

    get currentSide() {
        return this[this.side];
    }

    get translations() { return this.currentSide.translations; }

    get textElement() {
        return this.currentSide.textElement;
    }

    get side() {
        return this.sides[0];
    }
    get content() {
        return this.currentSide.content;
    }
    set content(val) {
        this.currentSide.content = val;
    }
    get textShadows() { return this.currentSide._textShadows; }
    set textShadows(val) { this.currentSide._textShadows = val; }
    get textOutline() { return this.currentSide._textOutline; }
    set textOutline(val) { this.currentSide._textOutline = val; }
    get scrollEnabled() { return this.currentSide._scrollEnabled; }
    set scrollEnabled(val) { this.currentSide._scrollEnabled = val; }
    get textBoxBgColor() { return this.currentSide._textBoxBgColor; }
    set textBoxBgColor(val) { this.currentSide._textBoxBgColor = val; }
    get textBoxBgOpacity() { return 100 }
    set textBoxBgOpacity(val) {  }
    get textBoxBorderColor() { return this.currentSide._textBoxBorderColor; }
    set textBoxBorderColor(val) { this.currentSide._textBoxBorderColor = val; }
    get textBoxBorderWidth() { return this.currentSide._textBoxBorderWidth; }
    set textBoxBorderWidth(val) { this.currentSide._textBoxBorderWidth = val; }
    get lineHeight() { return this.currentSide._lineHeight; }
    set lineHeight(val) { this.currentSide._lineHeight = val; }
    get fontSize() { return this.currentSide._fontSize; }
    set fontSize(val) { this.currentSide._fontSize = val; }

    _initValues(options) {
        this._scrollEnabled = true;
        this._extraStyles = "extraStyles" in options ? options.extraStyles : this._extraStyles;
        this._userRenamed = "userRenamed" in options !== undefined ? options.userRenamed : this._userRenamed;
        this._fontSize = "fontSize" in options !== undefined ? options.fontSize : this._fontSize;
        this.sides.forEach(side => {
            this[side] = new Side(side, this);
        });
        this._sizeSm = options.sizeSm ? options.sizeSm : {
            x: 0.05,
            y: 0.05,
            fontSize: this._fontSize,
            width: 0.5,
            height: 0.075,
        };

        this._sizeLg = options.sizeLg ? options.sizeLg : {
            x: 0.1,
            y: 0.1,
            width: 0.25,
            height: 0.35,
            fontSize: this._fontSize,
        };

    }

    flip() {
        [this.sides[0], this.sides[1]] = [this.sides[1], this.sides[0]]
        this.redraw();
        this.flashCard.classList.toggle("is-flipped");
    }

    constructor(options, parentLayer, uuid) {
        super(options, parentLayer, uuid);
        this.domElement.classList.add("q3-text-editor");
        this.domElement.style.perspective = "100vw";
        this.domElement.style.overflow = "visible";
        this.flashCard = document.createElement("div");
        this.sides.forEach(side => {
            this.flashCard.appendChild(this[side].textElement)
        });
        this.flipBtn = document.createElement("div");
        this.flipBtn.className = "flip-btn";
        this.domElement.appendChild(this.flipBtn);
        this[Q3.editMode ? "flipBtn" : "domElement"].addEventListener("click", (e) => {
            e.stopImmediatePropagation();
            this.flip();
        });

        this.flashCard.className = "flashcard-card";
        this.flashCard.appendChild(this.textElement);
        this.domElement.appendChild(this.flashCard);
    }

    getData(param) {
        return ["textBoxBgColor", "textBoxBorderColor", "textBoxBorderWidth", "content"].indexOf(param) !== -1 ? this.data[this.side] : this.data;
    }

    redraw(width, height) {
        !width && (width = RenderManager.domElement.width / RenderManager.pixelRatio);
        !height && (height = (RenderManager.domElement.height) / RenderManager.pixelRatio);

        let currentSize = this.getVWSize();

        if (this[currentSize]) { Object.assign(this, this[currentSize]); }

        const { x, y, w: elWidth, h: elHeight } = this.getRenderPositionAndSize(width, height);

        if (Q3.editMode) {
            if (Q3.mobileView && this.hiddenMobile) {
                this.textElement.style.opacity = 0.4;
            } else if (!Q3.mobileView && this.hiddenDesktop) {
                this.textElement.style.opacity = 0.4;
            } else {
                this.textElement.style.opacity = 1;
            }
        }

        // this.textElement.style.width = elWidth + "px";
        // this.textElement.style.height = elHeight + "px";

        this.checkSelected();

        this.domElement.style.width = elWidth + "px";
        this.domElement.style.height = elHeight + "px";
        this.domElement.style.left = (x || 0) + "px";
        this.domElement.style.top = (y || 0) + "px";

        let textElement = this.textElement;
        let newText = this.content;
        textElement.style.fontSize = parseFloat(this[currentSize].fontSize) / 24 + "rem";

        if (newText !== textElement.innerHTML) textElement.innerHTML = newText;

        let boxStyles = {
            backgroundColor: this.backgroundColor,
            border: this.border
        };

        this.lineHeight && (boxStyles.lineHeight = this.lineHeight);

        let extraStyles = Object.assign({}, this.extraStyles);
        if (extraStyles.paddingBottom) {
            extraStyles.paddingBottom = parseFloat(extraStyles.paddingBottom)+48 + "rem"
        }
        [
            "paddingBottom",
            "paddingLeft",
            "paddingRight",
            "paddingTop"
        ].forEach(p => {
            if (extraStyles[p]) {
                extraStyles[p] = parseFloat(extraStyles[p]) / 24 + "rem"
            }
        });

        extraStyles.borderRadius = parseFloat(extraStyles.borderRadius) / 15 + "rem";
        Object.assign(boxStyles, extraStyles);

        if (Q3.assigningLine) {
            boxStyles.backgroundColor = "";
            boxStyles.border = "";
        }

        Object.assign(this.textElement.style, boxStyles);
        if (boxStyles.borderRadius) {
            this.domElement.style.borderRadius = boxStyles.borderRadius;
        }

        if (this.objChanged) {
            Events.fire("objchanged");
            this.objChanged = false;
        }
    }


    render(width, height) {
        super.render(width, height);
    }

}

export default FlashCard;
export { FlashCard };
